import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PRAYERS_BAUTISMO, PRAYERS_COMUNION } from '../data/prayers';
import './StampCustomForm.css';

function StampCustomForm({ onChange, onSubmit, selectedCategory }) {
  const minDaysFromToday = 12;
  const minSelectableDateObject = new Date();
  minSelectableDateObject.setHours(0, 0, 0, 0);
  minSelectableDateObject.setDate(minSelectableDateObject.getDate() + minDaysFromToday);
  const minSelectableDate = `${minSelectableDateObject.getFullYear()}-${String(
    minSelectableDateObject.getMonth() + 1,
  ).padStart(2, '0')}-${String(minSelectableDateObject.getDate()).padStart(2, '0')}`;
  const nameLabel = selectedCategory === 'Comunion' ? 'Nombre del niño/a' : 'Nombre del bebe';
  const prayerOptions = selectedCategory === 'Comunion' ? PRAYERS_COMUNION : PRAYERS_BAUTISMO;
  const [form, setForm] = useState({
    genero: '',
    name: '',
    date: '',
    text: '',
    capilla: '',
    madrina: '',
    padrino: '',
    aclaraciones: '',
  });

  const [isSaved, setIsSaved] = useState(false);
  const [dateError, setDateError] = useState('');

  const isDateValid = (value) => {
    if (!value) {
      return { valid: false, message: '' };
    }

    const selectedDate = new Date(`${value}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffMs = selectedDate.getTime() - today.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < minDaysFromToday) {
      return {
        valid: false,
        message: `Fecha debe ser al menos ${minDaysFromToday} días desde hoy`,
      };
    }

    return { valid: true, message: '' };
  };

  // Verifica si todos los campos están llenos
  const requiresSponsors = selectedCategory === 'Bautismo';
  const isFormValid =
    form.genero !== '' &&
    form.name.trim() !== '' &&
    form.date.trim() !== '' &&
    form.text.trim() !== '' &&
    form.capilla.trim() !== '' &&
    (!requiresSponsors ||
      (form.madrina.trim() !== '' && form.padrino.trim() !== '')) &&
    dateError === '';

  useEffect(() => {
    onChange({ ...form, isSaved, isValid: isFormValid });
  }, [form, isSaved, isFormValid, onChange]);

  useEffect(() => {
    // Las oraciones disponibles dependen de la categoría, así que la
    // selección previa deja de ser válida al cambiar de una a otra.
    setForm((prev) => ({
      ...prev,
      text: '',
      ...(selectedCategory !== 'Bautismo' ? { madrina: '', padrino: '' } : {}),
    }));
    setIsSaved(false);
  }, [selectedCategory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setIsSaved(false);

    if (name === 'date') {
      const validation = isDateValid(value);
      setDateError(validation.message);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validation = isDateValid(form.date);
    setDateError(validation.message);
    if (!isFormValid || !validation.valid) return;
    setIsSaved(true);
    onSubmit(form);
  };

  return (
    <>
      <p>Esta es la información que necesitamos de ti , para completar el lado reverso de la estampita.</p>
      <form className="poster-form" id="stamp-custom-form" onSubmit={handleSubmit}>
        <label>¿Es niña o varón?</label>
        <div className="gender-radio-group">
          <label className="gender-radio-option">
            <input
              type="radio"
              name="genero"
              value="nina"
              checked={form.genero === 'nina'}
              onChange={handleChange}
              required
            />
            Niña
          </label>
          <label className="gender-radio-option">
            <input
              type="radio"
              name="genero"
              value="nino"
              checked={form.genero === 'nino'}
              onChange={handleChange}
              required
            />
            Varón
          </label>
        </div>

        <label htmlFor="name">{nameLabel}</label>
        <input
          id="name"
          name="name"
          type="text"
          value={form.name}
          onChange={handleChange}
          required
        />

        <label htmlFor="date">Fecha</label>
        <input
          id="date"
          name="date"
          type="date"
          className="date-input"
          value={form.date}
          min={minSelectableDate}
          onChange={handleChange}
          required
        />
        {!(form.date && !dateError) && (
          <p className="date-hint">Solo se habilitan fechas desde {minDaysFromToday} días en adelante.</p>
        )}
        {dateError && <p className="map-error">{dateError}</p>}

        <label htmlFor="text">Oración</label>
        <select
          id="text"
          name="text"
          value={form.text}
          onChange={handleChange}
          required
        >
          <option value="" disabled>
            Selecciona una oración
          </option>
          {prayerOptions.map((prayer) => (
            <option key={prayer} value={prayer}>
              {prayer.replace(/\s*\n\s*/g, ' ')}
            </option>
          ))}
        </select>
        {form.text && <p className="prayer-preview">{form.text}</p>}

        <label htmlFor="capilla">Capilla o Parroquia y Ciudad</label>
        <input
          id="capilla"
          name="capilla"
          type="text"
          value={form.capilla}
          onChange={handleChange}
          required
        />

        {requiresSponsors && (
          <>
            <label htmlFor="madrina">Nombre de la madrina</label>
            <input
              id="madrina"
              name="madrina"
              type="text"
              value={form.madrina}
              onChange={handleChange}
              required
            />

            <label htmlFor="padrino">Nombre del padrino</label>
            <input
              id="padrino"
              name="padrino"
              type="text"
              value={form.padrino}
              onChange={handleChange}
              required
            />
          </>
        )}

        <label htmlFor="aclaraciones">Aclaraciones</label>
        <textarea
          id="aclaraciones"
          name="aclaraciones"
          rows={4}
          value={form.aclaraciones}
          onChange={handleChange}
        />

      </form>
    </>
  );
}

StampCustomForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  selectedCategory: PropTypes.string.isRequired,
};

export default StampCustomForm;
